import { useRef } from "react";
import branding from "./branding";
import ChemistryToolkit from "./ChemistryToolkit";
import MathToolkit from "./MathToolkit";
import PhysicsToolkit from "./PhysicsToolkit";
import {
  formatMathematicsHtml,
} from "./mathematicsDisplayFormatter";
import "./Tutor.css";

function formatScienceHtml(text) {
  if (!text) {
    return "";
  }

  return text
    .replace(
      /\[H\+\]/g,
      "[H<sup>+</sup>]"
    )
    .replace(
      /\[OH-\]/g,
      "[OH<sup>−</sup>]"
    )
    .replace(
      /\[OH−\]/g,
      "[OH<sup>−</sup>]"
    )
    .replace(
      /dm(?:-3|−3|⁻³)/g,
      "dm<sup>−3</sup>"
    )
    .replace(
      /mol(?:-1|−1|⁻¹)/g,
      "mol<sup>−1</sup>"
    );
}

function Tutor({
  language,
  userEmail,
  messages,
  input,
  setInput,
  handleSend,
  insertSymbol,
  score,
  currentQuestion,
  mode,
  isProcessingAnswer,
  isPaperComplete,
  onBackToDashboard,
  selectedSubject,
}) {

  const isPhysics =
    selectedSubject?.paperCode ===
    "physical_sciences_p1";
  const isChemistry =
    selectedSubject?.paperCode ===
    "physical_sciences_p2";
  const isMathematics =
    selectedSubject?.subjectCode ===
    "mathematics";
  const answerInputRef = useRef(null);
  const tutorIcon =
    selectedSubject?.icon || "🧪";
  const tutorName = isPhysics
    ? "PhysicsMaster SA"
    : isMathematics
      ? "MathMaster SA"
      : branding.appName;

  const insertAtCursor = (symbol) => {
    const inputElement = answerInputRef.current;
    const selectionStart =
      inputElement?.selectionStart ?? input.length;
    const selectionEnd =
      inputElement?.selectionEnd ?? selectionStart;

    insertSymbol(
      symbol,
      selectionStart,
      selectionEnd
    );

    const cursorPosition =
      selectionStart + symbol.length;

    window.requestAnimationFrame(() => {
      if (!answerInputRef.current) {
        return;
      }

      answerInputRef.current.focus();
      answerInputRef.current.setSelectionRange(
        cursorPosition,
        cursorPosition
      );
    });
  };

  const modeLabel =
    language === "af"
      ? {
        areas: "Gebiede vir verbetering",
        standard: "Standaard",
        challenging: "Uitdagend",
        exam: "Eksamen",
      }[mode]
      : {
        areas: "Areas for Improvement",
        standard: "Standard",
        challenging: "Challenging",
        exam: "Exam",
      }[mode];

  return (
    <>
      <div
        style={{
          background: branding.primaryColor,
          color: "white",
        }}
      >
        <div
          style={{
            maxWidth: "1000px",
            margin: "0 auto",
            padding: "15px 20px",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <div>
            <h2 style={{ margin: 0 }}>
              {tutorIcon} {tutorName}
            </h2>

            <small>{userEmail}</small>
          </div>

          <button
            type="button"
            onClick={onBackToDashboard}
            style={{
              padding: "10px 15px",
              border: "none",
              borderRadius: "8px",
              cursor: "pointer",
              fontWeight: "bold",
            }}
          >
            {language === "af" ? "Dashboard" : "Dashboard"}
          </button>
        </div>
      </div>
      <div
        style={{
          maxWidth: "1000px",
          margin: "30px auto",
          padding: "20px",
        }}
      >
        <div
          style={{
            background: "white",
            borderRadius: "15px",
            padding: "20px",
            minHeight: "500px",
            boxShadow: "0 5px 15px rgba(0,0,0,0.08)",
          }}
        >
          <h3>
            🎓 {tutorName}
            <br />

            {language === "af"
              ? 'Tik "Start" om jou tutorsessie te begin.'
              : 'Type "Start" to begin your tutoring session.'}
          </h3>

          <div
            style={{
              marginTop: "20px",
              minHeight: "350px",
              border: "1px solid #ddd",
              borderRadius: "10px",
              padding: "15px",
              overflowY: "auto",
              background: "#fafafa",
            }}
          >
            {messages.map((message, index) => (
              <div
                key={index}
                style={{
                  display: "flex",
                  justifyContent: "flex-start",
                  marginBottom: "15px",
                }}
              >
                <div
                  className="tutor-message-bubble"
                  style={{
                    background:
                      message.sender === "user"
                        ? "#dbeafe"
                        : "#f3f4f6",
                    color:
                      message.sender === "user"
                        ? "#1e3a8a"
                        : "#111827",
                    padding: "12px",
                    borderRadius: "15px",
                  }}
                >
                  <div
                    style={{
                      fontSize: "12px",
                      fontWeight: "bold",
                      marginBottom: "5px",
                    }}
                  >
                    {message.sender === "user"
                      ? language === "af"
                        ? "👤 Leerder"
                        : "👤 Learner"
                      : `${tutorIcon} ${tutorName}`}
                  </div>

                  {message.sender === "user" ? (
                    <div
                      style={{
                        whiteSpace: "pre-wrap",
                        lineHeight: "1.5",
                      }}
                    >
                      {message.text}
                    </div>
                  ) : (
                    <div
                      className="tutor-message-html"
                      dangerouslySetInnerHTML={{
                        __html: isMathematics
                          ? formatMathematicsHtml(
                            message.text
                          )
                          : formatScienceHtml(
                            message.text
                          ),
                      }}
                    />
                  )}

                </div>
              </div>
            ))}
          </div>

          {isPaperComplete ? (
            <div
              style={{
                marginTop: "20px",
                padding: "20px",
                borderRadius: "12px",
                background: "#f3f4f6",
                textAlign: "center",
                fontWeight: "bold",
              }}
            >
              {language === "af"
                ? "🏁 Einde van vraestel"
                : "🏁 End of paper"}
            </div>
          ) : (
            <>
              {isPhysics ? (
                <PhysicsToolkit
                  language={language}
                  onInsertSymbol={insertAtCursor}
                />
              ) : isChemistry ? (
                <ChemistryToolkit
                  language={language}
                  onInsertSymbol={insertAtCursor}
                />
              ) : isMathematics ? (
                <MathToolkit
                  language={language}
                  paperNumber={
                    selectedSubject.paperNumber
                  }
                  onInsertSymbol={insertAtCursor}
                />
              ) : null}

              <div
                style={{
                  marginTop: "20px",
                  display: "flex",
                  gap: "10px",
                }}
              >
                <textarea
                  ref={answerInputRef}
                  value={input}
                  onChange={(event) =>
                    setInput(event.target.value)
                  }
                  placeholder={
                    language === "af"
                      ? "Tik jou volledige antwoord hier...\nVir vrae met subafdelings, sluit elke nommer by jou antwoord in, byvoorbeeld:\n1.1 ...\n1.2 ...\n1.3 ...\n\nFormule:\nSubstitusie:\nBerekening:\nFinale antwoord:"
                      : "Type your complete answer here...\nFor questions with subparts, include each number in your answer, for example:\n1.1 ...\n1.2 ...\n1.3 ...\n\nFormula:\nSubstitution:\nCalculation:\nFinal answer:"
                  }
                  rows={10}
                  style={{
                    flex: 1,
                    padding: "15px",
                    borderRadius: "10px",
                    border: "1px solid #ccc",
                    resize: "vertical",
                    fontFamily: "Arial, sans-serif",
                    lineHeight: "1.5",
                  }}
                />

                <button
                  type="button"
                  onClick={handleSend}
                  disabled={isProcessingAnswer}
                  style={{
                    background: isProcessingAnswer
                      ? "#9ca3af"
                      : branding.primaryColor,
                    color: "white",
                    border: "none",
                    borderRadius: "10px",
                    padding: "15px 25px",
                    cursor: isProcessingAnswer
                      ? "not-allowed"
                      : "pointer",
                  }}
                >
                  {isProcessingAnswer
                    ? language === "af"
                      ? "Verwerk..."
                      : "Processing..."
                    : language === "af"
                      ? "Stuur"
                      : "Send"}
                </button>

                <button
                  type="button"
                  onClick={() => setInput("")}
                  style={{
                    background: "#dc2626",
                    color: "white",
                    border: "none",
                    borderRadius: "10px",
                    padding: "15px 25px",
                    cursor: "pointer",
                  }}
                >
                  {language === "af"
                    ? "Vee uit"
                    : "Clear"}
                </button>
              </div>
            </>
          )}
        </div>

        <div
          style={{
            display: "flex",


            gap: "20px",
            marginTop: "20px",
          }}
        >
          <div
            style={{
              background: "white",
              flex: 1,
              padding: "20px",
              borderRadius: "12px",
              boxShadow: "0 5px 15px rgba(0,0,0,0.08)",
            }}
          >
            <h4>
              {language === "af" ? "🏆 Punt" : "🏆 Score"}
            </h4>

            <p>{score}</p>
          </div>

          <div
            style={{
              background: "white",
              flex: 1,
              padding: "20px",
              borderRadius: "12px",
              boxShadow: "0 5px 15px rgba(0,0,0,0.08)",
            }}
          >
            <h4>
              {language === "af" ? "📄 Vraag" : "📄 Question"}
            </h4>

            <p>
              {isPaperComplete
                ? currentQuestion
                : currentQuestion + 1}
            </p>
          </div>

          <div
            style={{
              background: "white",
              flex: 1,
              padding: "20px",
              borderRadius: "12px",
              boxShadow: "0 5px 15px rgba(0,0,0,0.08)",
            }}
          >
            <h4>
              {language === "af" ? "🎯 Modus" : "🎯 Mode"}
            </h4>

            <p>
              {mode
                ? modeLabel
                : language === "af"
                  ? "Nog nie gekies nie"
                  : "Not selected yet"}
            </p>
          </div>
        </div>
      </div>
    </>
  );
}

export default Tutor;
